import { useShow, useCustomMutation, useNotification } from '@refinedev/core';
import { Edit } from '@refinedev/antd';
import {
  Card,
  Descriptions,
  Tag,
  Row,
  Col,
  Timeline,
  Divider,
  Select,
  InputNumber,
  Input,
  Button,
  Form,
  Space,
  Modal,
} from 'antd';
import {
  EnvironmentOutlined,
  CarOutlined,
  UserOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { PostageTripDetails, CargoTripDetails } from './components';

interface Trip {
  id: number;
  tripType: string;
  status: string;
  pickupAddress: string;
  destinationAddress: string;
  pickupLat: number;
  pickupLong: number;
  destinationLat: number;
  destinationLong: number;
  distanceKm: number;
  priceEstimate: number;
  clientOfferPrice: number | null;
  finalPrice: number | null;
  paymentMethod: string | null;
  surgeMultiplier: number | null;
  passengerCount: number;
  requestedAt: string;
  acceptedAt: string | null;
  arrivedAt: string | null;
  pickedUpAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
  client: {
    id: number;
    fullName: string;
    phone: string;
    photoUrl: string | null;
  };
  driver: {
    id: number;
    fullName: string;
    phone: string;
    photoUrl: string | null;
    rating: number;
  } | null;
  vehicle: {
    make: string;
    model: string;
    plate: string;
    color: string;
  } | null;
}

const WAITING_STATUSES = ['REQUESTED', 'ACCEPTED', 'DRIVER_ARRIVED'];

const formatWaitingTime = (requestedAt: string): string => {
  const diff = Date.now() - new Date(requestedAt).getTime();
  if (diff < 0) return '0s';
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}k ${hours}s ${minutes}d`;
  if (hours > 0) return `${hours}s ${minutes}d`;
  if (minutes > 0) return `${minutes}d ${seconds}s`;
  return `${seconds}s`;
};

const formatDate = (date: string | null) => {
  if (!date) return '-';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${mins}`;
};

const formatCurrency = (value: number | string) => {
  return new Intl.NumberFormat('uz-UZ').format(Number(value)) + ' so\'m';
};

const statusOptions = [
  { value: 'REQUESTED', label: 'So\'ralgan' },
  { value: 'ACCEPTED', label: 'Qabul qilingan' },
  { value: 'DRIVER_ARRIVED', label: 'Haydovchi yetib keldi' },
  { value: 'PICKED_UP', label: 'Olib ketildi' },
  { value: 'ON_THE_WAY', label: 'Yo\'lda' },
  { value: 'IN_PROGRESS', label: 'Jarayonda' },
  { value: 'COMPLETED', label: 'Yakunlangan' },
  { value: 'CANCELLED', label: 'Bekor qilingan' },
  { value: 'INTERRUPTED', label: 'To\'xtatilgan' },
  { value: 'NO_SHOW', label: 'Kelmadi' },
];

const statusColors: Record<string, string> = {
  REQUESTED: 'orange',
  ACCEPTED: 'blue',
  DRIVER_ARRIVED: 'cyan',
  PICKED_UP: 'geekblue',
  ON_THE_WAY: 'processing',
  IN_PROGRESS: 'processing',
  COMPLETED: 'green',
  CANCELLED: 'red',
  INTERRUPTED: 'volcano',
  NO_SHOW: 'magenta',
};

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: 'Shahar ichida taksi',
  INTERREGION_TAXI: 'Shaharlararo taksi',
  POSTAGE: 'Pochta',
  CARGO: 'Yuk tashish',
};

export const TripEdit = () => {
  const { tripType, id } = useParams();
  const navigate = useNavigate();
  const { open: openNotification } = useNotification();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [, setTick] = useState(0);

  const { queryResult } = useShow<Trip>({
    resource: `admin/trips/${tripType}`,
    id: id!,
  });
  const { data, isLoading } = queryResult;
  const trip = data?.data;

  const { mutate: updateTrip } = useCustomMutation();

  // Live timer for waiting trips
  useEffect(() => {
    if (trip && WAITING_STATUSES.includes(trip.status)) {
      const interval = setInterval(() => setTick((t) => t + 1), 1000);
      return () => clearInterval(interval);
    }
  }, [trip?.status]);

  // Set form values when trip loads
  useEffect(() => {
    if (trip) {
      form.setFieldsValue({
        status: trip.status,
        pickupAddress: trip.pickupAddress,
        destinationAddress: trip.destinationAddress,
        finalPrice: trip.finalPrice,
        clientOfferPrice: trip.clientOfferPrice,
        passengerCount: trip.passengerCount,
        cancelReason: trip.cancelReason,
      });
    }
  }, [trip, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Only send changed fields
      const changes: Record<string, unknown> = {};
      if (values.status !== trip?.status) changes.status = values.status;
      if (values.pickupAddress !== trip?.pickupAddress) changes.pickupAddress = values.pickupAddress;
      if (values.destinationAddress !== trip?.destinationAddress) changes.destinationAddress = values.destinationAddress;
      if (values.finalPrice !== trip?.finalPrice) changes.finalPrice = values.finalPrice;
      if (values.clientOfferPrice !== trip?.clientOfferPrice) changes.clientOfferPrice = values.clientOfferPrice;
      if (values.passengerCount !== trip?.passengerCount) changes.passengerCount = values.passengerCount;
      if (values.cancelReason !== trip?.cancelReason) changes.cancelReason = values.cancelReason;

      if (Object.keys(changes).length === 0) {
        openNotification?.({
          type: 'warning',
          message: 'O\'zgarishlar yo\'q',
          description: 'Hech qanday o\'zgarish kiritilmagan',
        });
        return;
      }

      // Confirm if cancelling
      if (changes.status === 'CANCELLED' && trip?.status !== 'CANCELLED') {
        Modal.confirm({
          title: 'Sayohatni bekor qilish',
          content: 'Haqiqatan ham bu sayohatni bekor qilmoqchimisiz?',
          okText: 'Ha, bekor qilish',
          cancelText: 'Yo\'q',
          okButtonProps: { danger: true },
          onOk: () => performSave(changes),
        });
        return;
      }

      await performSave(changes);
    } catch {
      // form validation error
    }
  };

  const performSave = async (changes: Record<string, unknown>) => {
    setSaving(true);
    updateTrip(
      {
        url: `/admin/trips/${tripType}/${id}`,
        method: 'put',
        values: changes,
      },
      {
        onSuccess: () => {
          openNotification?.({
            type: 'success',
            message: 'Muvaffaqiyatli',
            description: 'Sayohat yangilandi',
          });
          queryResult.refetch();
        },
        onError: (error: any) => {
          openNotification?.({
            type: 'error',
            message: 'Xatolik',
            description: error?.message || 'Sayohatni yangilashda xatolik yuz berdi',
          });
        },
        onSettled: () => {
          setSaving(false);
        },
      },
    );
  };

  const getTimelineItems = () => {
    const items = [];

    if (trip?.requestedAt) {
      items.push({
        color: 'blue',
        children: `So'ralgan: ${formatDate(trip.requestedAt)}`,
      });
    }
    if (trip?.acceptedAt) {
      items.push({
        color: 'cyan',
        children: `Qabul qilingan: ${formatDate(trip.acceptedAt)}`,
      });
    }
    if (trip?.arrivedAt) {
      items.push({
        color: 'purple',
        children: `Haydovchi yetib keldi: ${formatDate(trip.arrivedAt)}`,
      });
    }
    if (trip?.pickedUpAt) {
      items.push({
        color: 'geekblue',
        children: `Olib ketildi: ${formatDate(trip.pickedUpAt)}`,
      });
    }
    if (trip?.startedAt) {
      items.push({
        color: 'orange',
        children: `Yo'lda: ${formatDate(trip.startedAt)}`,
      });
    }
    if (trip?.completedAt) {
      items.push({
        color: 'green',
        children: `Yakunlangan: ${formatDate(trip.completedAt)}`,
      });
    }
    if (trip?.canceledAt) {
      items.push({
        color: 'red',
        children: `Bekor qilingan: ${formatDate(trip.canceledAt)}`,
      });
    }

    return items;
  };

  return (
    <Edit
      isLoading={isLoading}
      title={`Sayohat #${trip?.id} - Tahrirlash`}
      headerButtons={
        <Space>
          <Button onClick={() => navigate(`/trips/${tripType}/${id}`)}>
            Ko'rish
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            Saqlash
          </Button>
        </Space>
      }
      footerButtons={
        <Space>
          <Button onClick={() => navigate(`/trips/${tripType}/${id}`)}>
            Bekor qilish
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            Saqlash
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            {/* Status Card */}
            <Card title="Holatni o'zgartirish" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Holat" name="status">
                    <Select
                      options={statusOptions}
                      optionRender={(option) => (
                        <Tag color={statusColors[option.value as string]}>
                          {option.label}
                        </Tag>
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Joriy holat">
                    <Tag
                      color={statusColors[trip?.status || ''] || 'default'}
                      style={{ fontSize: 14, padding: '4px 12px' }}
                    >
                      {statusOptions.find((s) => s.value === trip?.status)?.label || trip?.status}
                    </Tag>
                    {trip?.requestedAt && WAITING_STATUSES.includes(trip.status) && (
                      <Tag icon={<ClockCircleOutlined />} color="warning" style={{ marginLeft: 8 }}>
                        {formatWaitingTime(trip.requestedAt)}
                      </Tag>
                    )}
                  </Form.Item>
                </Col>
              </Row>
              {form.getFieldValue('status') === 'CANCELLED' && trip?.status !== 'CANCELLED' && (
                <Form.Item label="Bekor qilish sababi" name="cancelReason">
                  <Input.TextArea rows={2} placeholder="Bekor qilish sababini kiriting..." />
                </Form.Item>
              )}
            </Card>

            {/* Route Card */}
            <Card
              title={
                <>
                  <EnvironmentOutlined /> Marshrut
                </>
              }
              style={{ marginBottom: 24 }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Olish manzili" name="pickupAddress">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Borish manzili" name="destinationAddress">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>
              <Descriptions column={2}>
                <Descriptions.Item label="Masofa">
                  {Number(trip?.distanceKm || 0).toFixed(1)} km
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Payment Card */}
            <Card
              title={
                <>
                  <DollarOutlined /> To'lov
                </>
              }
              style={{ marginBottom: 24 }}
            >
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Taxminiy narx">
                    <span>{formatCurrency(trip?.priceEstimate || 0)}</span>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Mijoz taklifi" name="clientOfferPrice">
                    <InputNumber
                      style={{ width: '100%' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => Number(value!.replace(/,/g, ''))}
                      addonAfter="so'm"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Yakuniy narx" name="finalPrice">
                    <InputNumber
                      style={{ width: '100%' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => Number(value!.replace(/,/g, ''))}
                      addonAfter="so'm"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Descriptions column={2}>
                <Descriptions.Item label="To'lov usuli">
                  {trip?.paymentMethod || '-'}
                </Descriptions.Item>
                {trip?.surgeMultiplier && Number(trip.surgeMultiplier) > 1 && (
                  <Descriptions.Item label="Surge">
                    x{trip.surgeMultiplier}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Timeline */}
            <Card title="Vaqt chizig'i" style={{ marginBottom: 24 }}>
              <Timeline items={getTimelineItems()} />
              {trip?.cancelReason && (
                <>
                  <Divider />
                  <p>
                    <strong>Bekor qilish sababi:</strong> {trip.cancelReason}
                  </p>
                </>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            {/* Trip Info */}
            <Card style={{ marginBottom: 24 }}>
              <Descriptions column={1}>
                <Descriptions.Item label="Sayohat turi">
                  <Tag color="blue">{tripTypeLabels[trip?.tripType || ''] || trip?.tripType}</Tag>
                </Descriptions.Item>
              </Descriptions>
              <Form.Item label="Yo'lovchilar soni" name="passengerCount">
                <InputNumber min={1} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Card>

            {/* Client Info */}
            <Card
              title={
                <>
                  <UserOutlined /> Mijoz
                </>
              }
              style={{ marginBottom: 24 }}
            >
              <Descriptions column={1}>
                <Descriptions.Item label="Ism">
                  {trip?.client?.fullName || 'Ismi yo\'q'}
                </Descriptions.Item>
                <Descriptions.Item label="Telefon">
                  {trip?.client?.phone}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Driver Info */}
            {trip?.driver && (
              <Card
                title={
                  <>
                    <CarOutlined /> Haydovchi
                  </>
                }
                style={{ marginBottom: 24 }}
              >
                <Descriptions column={1}>
                  <Descriptions.Item label="Ism">
                    {trip.driver.fullName || 'Ismi yo\'q'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Telefon">
                    {trip.driver.phone}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reyting">
                    {trip.driver.rating != null ? Number(trip.driver.rating).toFixed(1) : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* Vehicle Info */}
            {trip?.vehicle && (
              <Card title="Avtomobil" style={{ marginBottom: 24 }}>
                <Descriptions column={1}>
                  <Descriptions.Item label="Mashina">
                    {trip.vehicle.make} {trip.vehicle.model}
                  </Descriptions.Item>
                  <Descriptions.Item label="Raqam">
                    {trip.vehicle.plate}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rang">
                    {trip.vehicle.color}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
